import { useEffect, useRef, useState } from "react";
import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import RequestSentDetailsDS from "../../DataServices/RequestSentDetailsDS";
import DatasetDS from "../../DataServices/DatasetDs";
import { useCustomContext } from "../CustomComponents/CustomComponents";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { insertLog } from "../Logs/InsertLog";
// Constants
export const agencySectionTemplates = [
  {
    id: "cheques",
    title: "Cheques",
    columns: ["Vin Id", "Amount", "Payable To", "Status", "Date", "Cheques"],
    records: [],
  },
  {
    id: "notes",
    title: "Notes",
    columns: ["Vin Id", "Note", "Added By", "Date", "Status", "Pinned"],
    records: [],
  },
  {
    id: "requests",
    title: "Requests",
    columns: ["Vin Id", "Request Type", "Request Email Address", "Response Type", "Response Email Address", "Status"],
    records: [],
  },
  {
    id: "events",
    title: "Events",
    columns: ["Vin Id", "Start Date Time", "End Date Time", "Message"],
    records: [],
  },
  {
    id: "callHistory",
    title: "Call History",
    columns: ["Vin Id", "Direction", "From", "To", "Duration", "Notes", "Status"],
    records: [],
  },
  {
    id: "mailHistory",
    title: "Mail History",
    columns: ["Vin Id", "Subject", "To", "Status", "Sent At"],
    records: [],
  },
];

export const getStatusClass = (status) => String(status || "").toLowerCase().replace(/\s+/g, "-");

export const DEFAULT_VISIBLE_COUNT = 5;
export const noteHighlightStatuses = ["Sent", "Escalated", "Closed-Data Received", "Closed-No Data", "Payment Pending", "Payment Closed"];

// Data services
const requestSentDS = new RequestSentDetailsDS();
const datasetDS = new DatasetDS();

const numberToWords = (amount) => {
  const integer = Math.floor(Number(amount) || 0);
  const decimal = Math.round(((Number(amount) || 0) - integer) * 100);
  return `${integer} DOLLARS${decimal > 0 ? ` AND ${decimal} CENTS` : ""}`;
};

// API/data helpers
const parseJsonValue = (value) => {
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

const unwrapApiPayload = (value) => {
  let payload = parseJsonValue(value);

  for (let index = 0; index < 4; index += 1) {
    if (payload && typeof payload === "object" && "body" in payload) {
      payload = parseJsonValue(payload.body);
      continue;
    }
    if (payload && typeof payload === "object" && typeof payload.data === "string") {
      payload = parseJsonValue(payload.data);
      continue;
    }
    break;
  }

  return payload || {};
};

const parseApiBody = (response) => {
  return unwrapApiPayload(response?.data || {});
};

const readArray = (source, keys) => {
  for (const key of keys) {
    const value = readNestedValue(source, key);
    if (Array.isArray(value)) return value;
  }
  return [];
};

const readNestedValue = (record, key) => {
  if (!record || !key) return undefined;
  return key.split(".").reduce((value, part) => {
    if (!value || typeof value !== "object") return undefined;
    if (part in value) return value[part];
    const matchedKey = Object.keys(value).find((itemKey) => itemKey.toLowerCase() === part.toLowerCase());
    return matchedKey ? value[matchedKey] : undefined;
  }, record);
};

const valueOf = (record, keys, fallback = "NA") => {
  for (const key of keys) {
    let value = readNestedValue(record, key);
    if (value && typeof value === "object" && "$date" in value) {
      value = value.$date;
    }
    if (value !== undefined && value !== null && value !== "") {
      return typeof value === "object" ? JSON.stringify(value) : value;
    }
  }
  return fallback;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const collectByKeyDeep = (value, keys) => {
  if (!value || typeof value !== "object") return [];
  const normalizedKeys = keys.map((key) => key.toLowerCase());
  const found = [];

  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }

    Object.entries(node).forEach(([key, child]) => {
      if (normalizedKeys.includes(key.toLowerCase())) {
        found.push(...toArray(child));
      }
      if (child && typeof child === "object") {
        visit(child);
      }
    });
  };

  visit(value);
  return found;
};

const collectNestedArrays = (records, keys) => [
  ...records.flatMap((record) => keys.flatMap((key) => toArray(readNestedValue(record, key)))),
  ...records.flatMap((record) => collectByKeyDeep(record, keys.filter((key) => !key.includes(".")))),
];

const collectChildrenWithParent = (records, keys) => (
  records.flatMap((record) => (
    keys.flatMap((key) => (
      toArray(readNestedValue(record, key)).map((item) => ({
        item,
        parent: record,
      }))
    ))
  ))
);

const uniqueBy = (items, getKey) => {
  const seen = new Set();
  return items.filter((item, index) => {
    const key = getKey(item, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getRecordsFromPayload = (payload) => {
  const body = unwrapApiPayload(payload);
  const candidates = [
    body,
    body?.records,
    body?.record?.records,
    body?.data?.records,
    body?.body?.records,
    body?.result?.records,
    body?.response?.records,
  ];

  for (const candidate of candidates) {
    const parsed = unwrapApiPayload(candidate);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.records)) return parsed.records;
  }

  const deepRecords = collectByKeyDeep(body, ["records"]);
  return deepRecords.length ? deepRecords : [];
};

const formatDurationValue = (value) => {
  if (typeof value === "string" && value.includes(":")) return value;
  const total = Math.floor(Number(value) || 0);
  const mins = String(Math.floor(total / 60)).padStart(2, "0");
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
};

const formatDisplayDate = (value, fallback = "NA") => {
  const rawValue = value && typeof value === "object" && "$date" in value ? value.$date : value;
  if (!rawValue) return fallback;
  const date = new Date(rawValue);
  return Number.isNaN(date.getTime()) ? rawValue : date.toLocaleString();
};

const parseAnalysisData = (item) => {
  const rawAnalysis = readNestedValue(item, "call_analysis_data");

  if (!rawAnalysis) return null;
  const parsed = parseJsonValue(rawAnalysis);

  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    return trimmed ? trimmed : null;
  }

  if (parsed && typeof parsed === "object") {
    return Object.keys(parsed).length > 0 ? parsed : null;
  }

  return parsed || null;
};

const getCallDirectionValue = (item) => {
  const directionValue = readNestedValue(item, "isIncoming")
    ?? readNestedValue(item, "incoming")
    ?? readNestedValue(item, "is_incoming");

  if (typeof directionValue === "boolean") return directionValue;
  if (typeof directionValue === "string") {
    const normalized = directionValue.toLowerCase().trim();
    if (["true", "incoming", "inbound"].includes(normalized)) return true;
    if (["false", "outgoing", "outbound"].includes(normalized)) return false;
  }

  const label = String(valueOf(item, ["direction", "callDirection", "call_direction"], "")).toLowerCase();
  if (label.includes("incoming") || label.includes("inbound")) return true;
  if (label.includes("outgoing") || label.includes("outbound")) return false;

  return null;
};

const getMailStatus = (item) => {
  if (typeof item?.isScheduled === "boolean") {
    if (!item.isScheduled) return "Sent";

    const scheduleTime = new Date(valueOf(item, ["scheduleTime"], "")).getTime();
    if (Number.isFinite(scheduleTime) && Date.now() >= scheduleTime) {
      return "Scheduled Sent";
    }

    return "Scheduled";
  }

  const status = String(valueOf(item, ["status", "mailStatus"], "") || "").toLowerCase();
  if (status === "sent") return "Sent";
  if (status === "scheduled sent" || status === "scheduled-sent") return "Scheduled Sent";
  if (status === "scheduled") return "Scheduled";
  return status ? status : "Sent";
};

const normalizeReqKey = (type) => {
  const value = String(type || "").toLowerCase().trim();
  if (value === "fax") return "fax";
  if (value === "email") return "emailReq";
  if (value === "snail mail") return "snail";
  if (value === "drop off") return "dropoff";
  if (value.includes("agency website")) return "agenweb";
  if (value.includes("crash")) return "crashdocx";
  return "";
};

const normalizeResKey = (type) => {
  const value = String(type || "").toLowerCase().trim();
  if (value.includes("fax")) return "fax";
  if (value.includes("email")) return "email";
  if (value.includes("pick")) return "pickup";
  if (value.includes("snail")) return "snailmail";
  if (value.includes("agency website")) return "agenweb";
  if (value.includes("crash")) return "crashdocs";
  return "";
};

// Request/response method detail render helpers
const renderAddressFields = (address = {}) => (
  <>
    <label style={{ marginTop: 8, display: "block" }}>Name</label>
    <input readOnly value={address.name || ""} />
    <label style={{ marginTop: 8, display: "block" }}>Address</label>
    <input readOnly value={address.address || ""} />
    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
      <div style={{ flex: 1 }}><label>City</label><input readOnly value={address.city || ""} /></div>
      <div style={{ flex: 1 }}><label>State</label><input readOnly value={address.state || ""} /></div>
      <div style={{ flex: 1 }}><label>Zip</label><input readOnly value={address.zip || ""} /></div>
    </div>
  </>
);

const renderMethodEmails = (label, values = []) => (
  (() => {
    const list = Array.isArray(values) ? values : values ? [values] : [];
    return (
      <>
        <label style={{ marginTop: 8, display: "block" }}>{label}</label>
        {list.length > 0 ? list.map((value, index) => (
          <input key={`${label}-${index}`} readOnly value={value} style={{ marginTop: 5 }} />
        )) : <input readOnly value="" style={{ marginTop: 5 }} />}
      </>
    );
  })()
);

const renderRequestMethodDetails = (requestMethod = {}) => {
  switch (normalizeReqKey(requestMethod.requestType)) {
    case "fax":
      return renderMethodEmails("Fax Numbers", requestMethod.faxSettings || []);
    case "emailReq":
      return renderMethodEmails("Email Addresses", requestMethod.emailSettings || []);
    case "snail": {
      const snail = requestMethod.snailMailSettings || {};
      return (
        <>
          {renderMethodEmails("Snail Mail Emails", snail.emailaddress || [])}
          {renderAddressFields(snail.address)}
        </>
      );
    }
    case "dropoff": {
      const drop = requestMethod.dropoffSettings || {};
      return (
        <>
          {renderMethodEmails("Drop Off Emails", drop.emailaddress || [])}
          {renderAddressFields(drop.address)}
        </>
      );
    }
    case "agenweb": {
      const agencyWebsite = requestMethod.agencyWebsiteSettings || {};
      return (
        <>
          <label style={{ marginTop: 8, display: "block" }}>Email</label>
          <input readOnly value={agencyWebsite.email || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Website URL</label>
          <input readOnly value={agencyWebsite.websiteURL || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Description</label>
          <input readOnly value={agencyWebsite.description || ""} />
        </>
      );
    }
    case "crashdocx": {
      const crashDocs = requestMethod.crashDocsWebsiteSettings || {};
      return (
        <>
          <label style={{ marginTop: 8, display: "block" }}>Email</label>
          <input readOnly value={crashDocs.email || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Website URL</label>
          <input readOnly value={crashDocs.websiteURL || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Description</label>
          <input readOnly value={crashDocs.description || ""} />
        </>
      );
    }
    default:
      return null;
  }
};

const renderResponseMethodDetails = (responseMethod = {}) => {
  switch (normalizeResKey(responseMethod.responseType)) {
    case "fax":
      return renderMethodEmails("Fax Numbers", responseMethod.faxSettings || []);
    case "email":
      return renderMethodEmails("Email Addresses", responseMethod.emailSettings || []);
    case "snailmail": {
      const snail = responseMethod.snailMailSettings || {};
      return (
        <>
          {renderMethodEmails("Snail Mail Emails", snail.emailaddress || [])}
          {renderAddressFields(snail.address)}
        </>
      );
    }
    case "pickup": {
      const pickup = responseMethod.pickupSettings || {};
      return (
        <>
          {renderMethodEmails("Pick Up Emails", pickup.emailaddress || [])}
          {renderAddressFields(pickup.address)}
        </>
      );
    }
    case "agenweb": {
      const agencyWebsite = responseMethod.agencyWebsiteSettings || {};
      return (
        <>
          <label style={{ marginTop: 8, display: "block" }}>Email</label>
          <input readOnly value={agencyWebsite.email || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Website URL</label>
          <input readOnly value={agencyWebsite.websiteURL || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Description</label>
          <input readOnly value={agencyWebsite.description || ""} />
        </>
      );
    }
    case "crashdocs": {
      const crashDocs = responseMethod.crashDocsWebsiteSettings || {};
      return (
        <>
          <label style={{ marginTop: 8, display: "block" }}>Email</label>
          <input readOnly value={crashDocs.email || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Website URL</label>
          <input readOnly value={crashDocs.websiteURL || ""} />
          <label style={{ marginTop: 8, display: "block" }}>Description</label>
          <input readOnly value={crashDocs.description || ""} />
        </>
      );
    }
    default:
      return null;
  }
};

// Agency detail normalization
const normalizeAgencyDetails = (body) => {
  const source = unwrapApiPayload(body?.record || body?.data || body || {});
  const sectionSource = unwrapApiPayload(source?.records || source);
  const hasSectionArrays = sectionSource && typeof sectionSource === "object" && !Array.isArray(sectionSource);
  const sectionArrayKeys = ["cheques", "notes", "events", "requests", "callHistory", "mailHistory", "emailHistory"];
  const recordList = Array.isArray(sectionSource)
    ? sectionSource
    : hasSectionArrays ? [] : getRecordsFromPayload(body).filter((record) => (
      !sectionArrayKeys.some((key) => Array.isArray(readNestedValue(record, key)))
    ));
  const directRequestRecords = readArray(sectionSource, ["requests", "requestDetails", "requestHistory"]);
  const requestRecords = directRequestRecords.length
    ? directRequestRecords
    : recordList.map((item) => item?.requests || item?.request || item);

  const chequeKeys = ["cheques", "chequeHistory", "chequeRecords", "cheque", "checks", "checkHistory", "check"];
  const chequeItems = [
    ...readArray(sectionSource, ["cheques", "chequeHistory", "chequeRecords", "checks", "checkHistory"])
      .map((item) => ({ item, parent: sectionSource })),
    ...collectChildrenWithParent(recordList, chequeKeys),
    ...collectChildrenWithParent(requestRecords, chequeKeys),
  ].filter(({ item }) => item && typeof item === "object");

  const noteKeys = ["notes", "noteHistory", "noteRecords"];

  const directNoteItems = readArray(sectionSource, ["notes", "noteHistory", "noteRecords"])
    .map((item) => ({ item, parent: item }));
  const noteItems = directNoteItems.length
    ? directNoteItems
    : requestRecords.flatMap((request) => {
      return noteKeys.flatMap((key) => {
        const notes = toArray(readNestedValue(request, key));

        return notes.map((note) => ({
          item: note,
          parent: request,
        }));
      });
    });

  const requestItems = requestRecords.length
    ? requestRecords
    : readArray(sectionSource, ["requests", "requestDetails", "requestHistory"]);

  const eventKeys = ["events", "eventHistory", "eventRecords", "event"];
  const eventItems = uniqueBy([
    ...collectChildrenWithParent([sectionSource], eventKeys),
    ...collectChildrenWithParent(recordList, eventKeys),
    ...collectChildrenWithParent(requestRecords, eventKeys),
  ].filter(({ item }) => item && typeof item === "object"), ({ item, parent }, index) => (
    valueOf(item, ["_id", "id", "eventID", "eventId"], `${valueOf(item, ["requestID", "requestId", "vinID", "vinId", "vin_id"], valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"], "request"))}-${valueOf(item, ["startDateTime", "startDate", "createdAt", "created_at"], index)}-${valueOf(item, ["endDateTime", "endDate", "updatedAt", "updated_at"], "")}-${valueOf(item, ["message", "description", "notes"], "")}`)
  ));

  const callKeys = ["callHistory", "calls", "call_history", "callRecords", "callDetails", "callInfo", "callInfos"];
  const callItems = uniqueBy([
    ...collectChildrenWithParent([sectionSource], callKeys),
    ...collectChildrenWithParent(recordList, callKeys),
    ...collectChildrenWithParent(requestRecords, callKeys),
  ].filter(({ item }) => item && typeof item === "object"), ({ item, parent }, index) => (
    valueOf(item, ["caller_id"], `${valueOf(item, ["requestID"], valueOf(parent, ["requestID"], "request"))}-${valueOf(item, ["updated_at", "updatedAt", "createdAt", "created_at"], index)}-${valueOf(item, ["called_from", "calledFrom", "from"], "")}-${valueOf(item, ["called_to", "calledTo", "to"], "")}-${valueOf(item, ["call_duration", "duration"], "")}`)
  ));

  const mailItems = [
    ...readArray(sectionSource, ["mailHistory"]),
    ...collectNestedArrays(recordList, ["mailHistory"]),
    ...collectNestedArrays(requestRecords, ["mailHistory"]),
  ];
  const sourceAgencyPhoneNumber = valueOf(source, ["agencyPhoneNumber"], "");

  const cheques = chequeItems.map(({ item, parent }, index) => ({
    id: valueOf(item, ["chequeID", "chequeId", "_id", "id", "s3Key", "chequeS3Key", "cheque_s3_key", "cheque.s3Key", "pdf.s3Key"], `CHQ-${index + 1}`),
    requestID: valueOf(item, ["requestID", "requestId", "vinID", "vinId", "cheque.requestID", "cheque.requestId"], valueOf(parent, ["requestID", "requestId", "vinID", "vinId"], "")),
    vinId: valueOf(item, ["vinID", "vinId", "vin", "requestID", "requestId", "cheque.vinID", "cheque.vinId", "cheque.requestID"], valueOf(parent, ["vinID", "vinId", "vin", "requestID", "requestId"])),
    amount: valueOf(item, ["amount", "chequeAmount", "checkAmount", "cheque.amount", "check.amount"]),
    payableTo: valueOf(item, ["payableTo", "paymentTo", "payee", "payTo", "cheque.payableTo", "cheque.paymentTo", "check.payableTo"], valueOf(parent, ["agencyName", "agencyDisplayName", "agency", "agencyID"])),
    status: valueOf(
      item,
      ["status", "chequeStatus", "checkStatus", "generationStatus", "cheque.status", "check.status"],
      valueOf(item, ["amount", "payableTo", "paymentTo", "payee", "cheque.amount", "cheque.payableTo"], "") ? "Generated" : "NA"
    ),
    date: valueOf(item, ["generatedOn", "generatedAt", "createdAt", "created_at", "updatedAt", "updated_at", "date", "cheque.generatedOn", "cheque.generatedAt", "cheque.createdAt"]),
    s3Key: valueOf(item, ["s3Key", "chequeS3Key", "checkS3Key", "cheque_s3_key", "check_s3_key", "pdfS3Key", "pdf_s3_key", "s3_key", "cheque.s3Key", "check.s3Key", "pdf.s3Key", "file.s3Key"], ""),
    raw: item,
  }));

  const notes = noteItems.map(({ item, parent }, index) => [
    valueOf(item, ["text", "notes"], ""),
    valueOf(item, ["updated_by_email_id", "addedBy", "created_by_email_id", "email"]),
    valueOf(item, ["addedTimestamp", "createdAt", "created_at", "date"]),
    valueOf(item, ["status"], ""),
    Boolean(item?.isPinned),
    valueOf(item, ["_id", "id", "noteID", "noteId"], `${valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"], "request")}-note-${index}`),
    valueOf(
      item,
      ["vinID", "vinId", "vin", "vin_id", "requestID", "requestId"],
      valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"], "NA")
    ),]);

  const requests = requestItems.map((item, index) => ({
    id: valueOf(item, ["_id", "id"], `request-${index}`),
    requestID: valueOf(item, ["requestID", "requestId", "vinID", "vinId", "vin_id"]),
    vinId: valueOf(item, ["vinID", "vinId", "vin", "vin_id", "requestID", "requestId"]),
    requestMethod: item?.requestMethod || {},
    responseMethod: item?.responseMethod || {},
    agencyName: valueOf(item, ["agencyName", "agencyDisplayName", "agency", "agencyID"]),
    agencyPhoneNumber: valueOf(item, ["agencyPhoneNumber"], sourceAgencyPhoneNumber),
    status: valueOf(item, ["outgoingRequest_Status", "status", "requestStatus", "request_status"], "Sent"),
    raw: item,
  }));

  const events = uniqueBy(
    eventItems.map(({ item, parent }) => [
      valueOf(item, ["vinId", "vinID", "VINID", "requestID"],
        valueOf(parent, ["request.requestID", "requestID"], "NA")
      ),
      valueOf(item, ["startDateTime", "startDate", "createdAt", "created_at"]),
      valueOf(item, ["endDateTime", "endDate", "updatedAt", "updated_at"]),
      valueOf(item, ["message", "description", "notes"]),
    ]),
    (record) => record.map((value) => String(value || "").trim()).join("|")
  );

  const callHistory = callItems.map(({ item, parent }, index) => {
    const analysis = parseAnalysisData(item);

    return {
      id: valueOf(item, ["_id", "id", "caller_id", "contactId"], `${valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"], "request")}-call-${index}`),
      vinId: valueOf(item, ["vinID", "vinId", "vin_id", "requestID", "requestId"]) || valueOf(parent?.request, ["requestID", "requestId", "vinID", "vinId", "vin_id"]) || valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"]) || "NA",
      requestID: valueOf(item, ["vinID", "vinId", "vin_id", "requestID", "requestId"]) || valueOf(parent?.request, ["requestID", "requestId", "vinID", "vinId", "vin_id"]) || valueOf(parent, ["requestID", "requestId", "vinID", "vinId", "vin_id"]) || "NA",
      agentEmail: valueOf(item, ["agent_email", "agentEmail", "direction", "callDirection"], "-"),
      from: valueOf(item, ["called_from", "calledFrom", "from"]),
      to: valueOf(item, ["called_to", "calledTo", "to"]),
      duration: formatDurationValue(valueOf(item, ["call_duration", "duration"], 0)),
      date: formatDisplayDate(valueOf(item, ["updated_at", "updatedAt", "createdAt", "created_at"], ""), "NA"),
      notes: valueOf(item, ["notes", "callNotes"], "No notes added."),
      status: valueOf(item, ["call_status", "status"], "NA"),
      recordingKey: valueOf(item, ["call_s3_key", "callS3Key", "recordingKey"], ""),
      incoming: getCallDirectionValue(item),
      analysis,
      raw: { ...item, call_analysis_data: analysis },
    };
  });

  const mailHistory = mailItems.map((item, index) => {
    const status = getMailStatus(item);
    const dateValue = status === "Sent"
      ? valueOf(item, ["sentAt", "createdAt", "created_at"])
      : valueOf(item, ["scheduleTime", "sentAt", "createdAt", "created_at"]);

    return {
      id: valueOf(item, ["_id", "id", "messageId"], `mail-${index}`),
      vinId: valueOf(
        item,
        ["vinID", "vinId", "vin", "requestID", "requestId", "vin_id"],
        "NA"
      ),
      subject: valueOf(item, ["subject"], "No Subject"),
      recipients: Array.isArray(item?.recipients) ? item.recipients.join(", ") : valueOf(item, ["to", "recipient", "recipients", "emailSettings"]),
      status,
      date: dateValue,
      raw: item,
    };
  });

  return [
    { ...agencySectionTemplates[0], records: cheques },
    { ...agencySectionTemplates[1], records: notes },
    { ...agencySectionTemplates[2], records: requests },
    { ...agencySectionTemplates[3], records: events },
    { ...agencySectionTemplates[4], records: callHistory },
    { ...agencySectionTemplates[5], records: mailHistory },
  ];
};

const agencySectionApiKeys = {
  cheques: "CHEQUES",
  notes: "NOTES",
  requests: "REQUESTS",
  events: "EVENTS",
  callHistory: "CALLHISTORY",
  mailHistory: "MAILHISTORY",
};

const agencySectionRecordKeys = {
  cheques: "cheques",
  notes: "notes",
  requests: "requests",
  events: "events",
  callHistory: "callHistory",
  mailHistory: "mailHistory",
};

const normalizeAgencySectionDetails = (sectionId, body) => {
  const source = unwrapApiPayload(body?.record || body?.data || body || {});
  const sectionKey = agencySectionRecordKeys[sectionId] || sectionId;
  const recordsPayload = unwrapApiPayload(source?.records || source);

  if (Array.isArray(recordsPayload)) {
    return normalizeAgencyDetails({ records: { [sectionKey]: recordsPayload } })
      .find((section) => section.id === sectionId);
  }

  return normalizeAgencyDetails(body).find((section) => section.id === sectionId);
};

// Hook

const agencyHistoryLogLabels = {
  callHistory: "call history",
  mailHistory: "mail history",
};



export const useAgencyDetailPageLogic = () => {

  const searchItem = JSON.parse(localStorage.getItem("agencyDetailsPayload") || "{}");
  const [datasetID, setDatasetID] = useState(searchItem.datasetID || "");
  const [datasetName, setDatasetName] = useState(searchItem.datasetName || "");
  const [agencyID, setAgencyID] = useState(searchItem.agencyID || "");
  const navigate = useNavigate();
  // const searchItem = JSON.parse(localStorage.getItem("agencyDetailsPayload") || "{}");
  const [datasets, setDatasets] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  // const [datasetID, setDatasetID] = useState(searchItem.dataSetId || searchItem.datasetID || "");
  // const [datasetName, setDatasetName] = useState(searchItem.datasetName || searchItem.dataSetName || "");
  // const [agencyID, setAgencyID] = useState(searchItem.agencyId || searchItem.agencyID || "");
  const [datasetSearch, setDatasetSearch] = useState("");
  const [agencySearch, setAgencySearch] = useState("");
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const [isAgencyDropdownOpen, setIsAgencyDropdownOpen] = useState(false);

  // Page data
  const [hasSearched, setHasSearched] = useState(false);
  const [agencySections, setAgencySections] = useState([]);
  const [openSections, setOpenSections] = useState([]);
  const [, setExpandedSections] = useState({});
  const [viewAllLoadedSections, setViewAllLoadedSections] = useState({});
  const [activeSectionModal, setActiveSectionModal] = useState(null);
  const [mailViewMode, setMailViewMode] = useState("grid");
  const [callHistoryIncoming, setCallHistoryIncoming] = useState(true);

  // Modals and audio
  const [selectedActionRequest, setSelectedActionRequest] = useState(null);
  const [dialOpen, setDialOpen] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [showViewChequeModal, setShowViewChequeModal] = useState(false);
  const [chequePdfUrl, setChequePdfUrl] = useState("");
  const [viewChequeRequestId, setViewChequeRequestId] = useState("");
  const [analysisCall, setAnalysisCall] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const navigationLogRef = useRef(false);
  const { hud, stopHudRotation, showAlert, showToast } = useCustomContext();
  const useremail = localStorage.getItem("email");
  const userName = useremail?.split("@")[0] || "";
  const [selectedMail, setSelectedMail] = useState(null);
  const [showMailHeaderDetails, setShowMailHeaderDetails] = useState(false);
  const mailHeaderToggleRef = useRef(null);
  const mailHeaderDetailsRef = useRef(null);
  const [addModalType, setAddModalType] = useState("");
  const [addNoteText, setAddNoteText] = useState("");
  const [addEventData, setAddEventData] = useState({ startDateTime: "", endDateTime: "", message: "" });
  const [addActionErrors, setAddActionErrors] = useState({});
  const [addChequeData, setAddChequeData] = useState({
    payableTo: "",
    amount: "",
    memo: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  // const fetchDatasets = async () => {
  //   try {
  //     const response = await datasetDS.AP_Dataset_List_GET({});
  //     const body = parseApiBody(response);
  //     const records = Array.isArray(body) ? body : Array.isArray(body?.records) ? body.records : [];
  //     setDatasets(records);
  //   } catch (error) {
  //     setDatasets([]);
  //     showAlert(error.message || "Failed to load datasets.", [
  //       { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
  //     ]);
  //   }
  // };

  // const fetchAgenciesByDataset = async (selectedDatasetID) => {
  //   if (!selectedDatasetID) {
  //     setAgencyOptions([]);
  //     return;
  //   }

  //   try {
  //     const response = await requestSentDS.WS_Agency_ByDatasetID_GET({ datasetID: selectedDatasetID });
  //     const body = parseApiBody(response);
  //     const records = Array.isArray(body) ? body : Array.isArray(body?.records) ? body.records : [];
  //     setAgencyOptions(records);
  //   } catch (error) {
  //     setAgencyOptions([]);
  //     showAlert(error.message || "Failed to load agencies.", [
  //       { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
  //     ]);
  //   }
  // };

  useEffect(() => {
    if (navigationLogRef.current) return;
    navigationLogRef.current = true;

    insertLog(
      `${userName} navigated to Agency Details screen successfully`,
      0,
      useremail,
      hud,
      stopHudRotation,
      showAlert,
      '',
      'GET',
      'AgencyDetailPage',
      'SUCCESS',
      ''
    );
  }, [hud, showAlert, stopHudRotation, useremail]);

  useEffect(() => {
    if (!datasetID || !agencyID) return;
    fetchAgencyDetails()
  }, [datasetID, agencyID]);

  useEffect(() => {
    if (!showMailHeaderDetails) return;
    const handler = (e) => {
      if (!mailHeaderToggleRef.current?.contains(e.target) &&
        !mailHeaderDetailsRef.current?.contains(e.target)) {
        setShowMailHeaderDetails(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMailHeaderDetails]);

  const getSenderDisplayName = (mailItem) => {
    const from = mailItem?.from || "";
    const local = from.includes("@") ? from.split("@")[0] : from;
    if (!local) return "Unknown Sender";
    return local.split(/[._-]/g).filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const getSenderInitial = (mailItem) => getSenderDisplayName(mailItem).charAt(0)?.toUpperCase() || "M";

  const formatMailDateTime = (value) => {
    if (!value) return "NA";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "NA";
    return date.toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true
    });
  };

  const getDatasetLabel = (dataset) => dataset?.datasetName || dataset?.datasetID || "";

  const getAgencyLabel = (agency) => {
    const agencyName = agency?.agencyName || agency?.agencyDisplayName || "";
    return agency?.agencyID ? `${agency.agencyID} - ${agencyName}` : agencyName;
  };

  const filteredDatasets = datasets.filter((dataset) => {
    const searchText = datasetSearch.toLowerCase();
    return (
      getDatasetLabel(dataset).toLowerCase().includes(searchText) ||
      String(dataset?.datasetID || "").toLowerCase().includes(searchText)
    );
  });

  const filteredAgencyOptions = agencyOptions.filter((agency) => {
    const searchText = agencySearch.toLowerCase();
    return (
      getAgencyLabel(agency).toLowerCase().includes(searchText) ||
      String(agency?.agencyID || "").toLowerCase().includes(searchText)
    );
  });

  const resetAgencySelection = () => {
    setAgencyID("");
    setAgencySearch("");
    setAgencyOptions([]);
    setIsAgencyDropdownOpen(false);
  };

  const selectDataset = (dataset) => {
    const selectedDatasetID = dataset?.datasetID || "";
    setDatasetID(selectedDatasetID);
    setDatasetName(dataset?.datasetName || dataset?.dataSetName || "");
    setDatasetSearch(getDatasetLabel(dataset));
    resetAgencySelection();
    setHasSearched(false);
    setAgencySections([]);
    setOpenSections([]);
    setExpandedSections({});
    setViewAllLoadedSections({});
    setActiveSectionModal(null);
    setSelectedActionRequest(null);
    setIsDatasetDropdownOpen(false);
    // fetchAgenciesByDataset(selectedDatasetID);
  };

  const handleDatasetSearchChange = (event) => {
    const value = event.target.value;
    setDatasetSearch(value);
    setIsDatasetDropdownOpen(true);

    if (datasetID) {
      setDatasetID("");
      resetAgencySelection();
      setHasSearched(false);
      setAgencySections([]);
      setOpenSections([]);
      setExpandedSections({});
      setViewAllLoadedSections({});
      setActiveSectionModal(null);
      setSelectedActionRequest(null);
    }
  };

  const handleDatasetKeyDown = (event) => {
    if (event.key === "Enter" && filteredDatasets.length > 0) {
      event.preventDefault();
      selectDataset(filteredDatasets[0]);
    }

    if (event.key === "Escape") {
      setIsDatasetDropdownOpen(false);
    }
  };

  const selectAgency = (agency) => {
    setAgencyID(agency?.agencyID || "");
    setAgencySearch(getAgencyLabel(agency));
    setHasSearched(false);
    setAgencySections([]);
    setOpenSections([]);
    setExpandedSections({});
    setViewAllLoadedSections({});
    setActiveSectionModal(null);
    setSelectedActionRequest(null);
    setIsAgencyDropdownOpen(false);
  };

  const handleAgencySearchChange = (event) => {
    const value = event.target.value;
    setAgencySearch(value);
    setIsAgencyDropdownOpen(true);

    if (agencyID) {
      setAgencyID("");
      setHasSearched(false);
      setAgencySections([]);
      setOpenSections([]);
      setExpandedSections({});
      setViewAllLoadedSections({});
      setActiveSectionModal(null);
      setSelectedActionRequest(null);
    }
  };

  const handleAgencyKeyDown = (event) => {
    if (event.key === "Enter" && filteredAgencyOptions.length > 0) {
      event.preventDefault();
      selectAgency(filteredAgencyOptions[0]);
    }

    if (event.key === "Escape") {
      setIsAgencyDropdownOpen(false);
    }
  };

  const fetchAgencyDetails = async () => {
    // if (!datasetID) {
    //   showToast({ message: "Please select a Dataset" });
    //   return;
    // }

    hud("Please Wait...");
    try {
      const response = await requestSentDS.WS_Agency_Details_Get({ agencyID, datasetID });
      if (response?.data?.statusCode && response.data.statusCode !== 200) {
        const body = parseApiBody(response);
        throw new Error(body?.message || "Failed to load agency details.");
      }
      const body = parseApiBody(response);
      const sections = normalizeAgencyDetails(body);

      setAgencySections(sections);
      setHasSearched(true);
      setOpenSections(sections.map((section) => section.id));
      setExpandedSections({});
      setViewAllLoadedSections({});
      setActiveSectionModal(null);
      setSelectedActionRequest(null);
    } catch (error) {
      insertLog(
        `${userName} failed to fetch agency details for agency ${agencyID || "NA"} in dataset ${datasetID || "NA"}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        agencyID || '',
        'GET',
        'WS_Agency_Details_Get',
        'FAILED',
        error.message
      );

      setAgencySections([]);
      setHasSearched(true);
      setOpenSections([]);
      setExpandedSections({});
      setViewAllLoadedSections({});
      setActiveSectionModal(null);
      showAlert(error.message || "Something went wrong while fetching agency details.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    await fetchAgencyDetails();
  };

  const toggleSection = (sectionId) => {
    setOpenSections((previous) =>
      previous.includes(sectionId)
        ? previous.filter((id) => id !== sectionId)
        : [...previous, sectionId]
    );
  };

  const openSectionModal = (sectionId, event) => {
    event?.stopPropagation();
    const isOpen = openSections.includes(sectionId);

    if (!isOpen) {
      setOpenSections((previous) => (
        previous.includes(sectionId) ? previous : [...previous, sectionId]
      ));
    }

    if (viewAllLoadedSections[sectionId]) {
      setActiveSectionModal(sectionId);
      return;
    }

    fetchViewAllSection(sectionId, true);
  };

  const closeSectionModal = () => {
    setActiveSectionModal(null);
  };

  const fetchAgencyAddress = async (agencyID) => {
    try {
      if (!agencyID) {
        showToast({ message: "Agency ID missing" });
        return;
      }
      hud("Please Wait...");
      const response = await requestSentDS.WS_Agency_Address_ById_GET({ agencyID });
      const data = response.data;
      const body = typeof data.body === "string" ? JSON.parse(data.body) : data.body;

      if (data.statusCode === 200 && body?.record) {
        const rec = body.record;
        setAddChequeData((prev) => ({
          ...prev,
          payableTo: rec.agencyDisplayName || "",
          address: rec.agencyAddress?.address || "",
          city: rec.agencyAddress?.city || "",
          state: rec.agencyAddress?.state || "",
          zip: rec.agencyAddress?.zip || ""
        }));

      } else {
        showToast({ message: "Agency record not found" });
        insertLog(
          `${userName} failed to fetch agency address for agency '${agencyID}'`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'GET',
          'WS_Agency_Address_ById_GET',
          'FAILED',
          'failed to fetch agency address'
        );
      }
    } catch (err) {
      insertLog(
        `${userName} failed to fetch agency address for agency '${agencyID}'`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        agencyID || '',
        'GET',
        'WS_Agency_Address_ById_GET',
        'FAILED',
        err.message
      );
      console.error("Error fetching agency address:", err);
      showToast({ message: "Error fetching agency details" });
    } finally { stopHudRotation(); }
  };

  const fetchViewAllSection = async (sectionId, openModal = false) => {
    if (!datasetID) {
      showToast({ message: "Please select a Dataset" });
      return false;
    }

    const sectionKey = agencySectionApiKeys[sectionId] || sectionId;
    hud("Please Wait...");

    try {
      const response = await requestSentDS.WS_Agency_Details_Get({
        agencyID,
        datasetID,
        key: sectionKey,
      });

      if (response?.data?.statusCode && response.data.statusCode !== 200) {
        const body = parseApiBody(response);
        throw new Error(body?.message || "Failed to load records.");
      }

      const body = parseApiBody(response);
      const updatedSection = normalizeAgencySectionDetails(sectionId, body);

      if (!updatedSection) {
        throw new Error("Failed to load records.");
      }

      setAgencySections((previous) => previous.map((section) => (
        section.id === sectionId ? { ...section, records: updatedSection.records } : section
      )));
      setOpenSections((previous) => (
        previous.includes(sectionId) ? previous : [...previous, sectionId]
      ));
      setViewAllLoadedSections((previous) => ({
        ...previous,
        [sectionId]: true,
      }));
      if (openModal) {
        setActiveSectionModal(sectionId);
      }
      return true;
    } catch (error) {
      showAlert(error.message || "Something went wrong while loading records.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
      return false;
    } finally {
      stopHudRotation();
    }
  };

  const getNoteKey = (record) => record[5] || `${record[0]}-${record[1]}-${record[2]}`;

  const isNotePinned = (record) => Boolean(record[4]);

  const getDefaultActionRequest = () => {
    const requestSection = agencySections.find((section) => section.id === "requests");
    return requestSection?.records?.[0] || null;
  };

  const clearAddActionError = (field) => {
    setAddActionErrors((previous) => {
      if (!previous[field]) return previous;
      const updated = { ...previous };
      delete updated[field];
      return updated;
    });
  };

  const validateAddNote = () => {
    const errors = {};
    if (!addNoteText.trim()) {
      errors.note = "Note is required";
    }
    setAddActionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateAddEvent = () => {
    const errors = {};
    const startDate = addEventData.startDateTime ? new Date(addEventData.startDateTime) : null;
    const endDate = addEventData.endDateTime ? new Date(addEventData.endDateTime) : null;
    const now = new Date();

    if (!addEventData.startDateTime) {
      errors.startDateTime = "Start date time is required";
    } else if (startDate && startDate < now) {
      errors.startDateTime = "Start date time cannot be in the past";
    }
    if (!addEventData.endDateTime) {
      errors.endDateTime = "End date time is required";
    }
    if (startDate && endDate && endDate < startDate) {
      errors.endDateTime = "End date time cannot be before start date time";
    }

    setAddActionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getCurrentDateTimeInputValue = () => {
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 16);
  };

  const validateAddCheque = () => {
    const errors = {};
    const amount = Number(addChequeData.amount);

    if (!addChequeData.payableTo.trim()) {
      errors.payableTo = "Payable To is required";
    }
    if (!addChequeData.amount.trim()) {
      errors.amount = "Amount is required";
    } else if (!Number.isFinite(amount) || amount <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    if (!addChequeData.address.trim()) {
      errors.address = "Address is required";
    }
    if (!addChequeData.city.trim()) {
      errors.city = "City is required";
    }
    if (!addChequeData.state.trim()) {
      errors.state = "State is required";
    }
    if (!addChequeData.zip.trim()) {
      errors.zip = "Zip is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(addChequeData.zip.trim())) {
      errors.zip = "Please enter a valid zip";
    }

    setAddActionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const closeAddActionModal = () => {
    setAddModalType("");
    setAddNoteText("");
    setAddEventData({ startDateTime: "", endDateTime: "", message: "" });
    setAddActionErrors({});
    setAddChequeData({
      payableTo: "",
      amount: "",
      memo: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    });
  };

  const openAddActionModal = async (sectionId, event) => {
    event?.stopPropagation();

    if (!agencyID || !datasetID) {
      showToast({ message: "Please select Dataset and Agency" });
      return;
    }

    setAddActionErrors({});
    setAddModalType(sectionId);

    if (sectionId === "notes") {
      setAddNoteText("");
    }

    if (sectionId === "cheques") {
      // default empty
      setAddChequeData({
        payableTo: "",
        amount: "",
        memo: "",
        address: "",
        city: "",
        state: "",
        zip: "",
      });

      // call agency address API
      await fetchAgencyAddress(agencyID);
    }
  };

  const handleAddNoteSubmit = async (event) => {
    event.preventDefault();
    if (!agencyID || !datasetID) {
      showToast({ message: "Please select Dataset and Agency" });
      return;
    }
    if (!validateAddNote()) {
      return;
    }

    hud("Adding Note...");
    try {
      const response = await requestSentDS.WS_Agency_Details_Add_Data({
        agencyID,
        datasetID,
        datasetName,
        notes: [
          {
            text: addNoteText.trim(),
            updated_by_email_id: localStorage.getItem("email") || "",
            isPinned: true,
          },
        ],
      });
      const body = parseApiBody(response);
      if (response?.data?.statusCode === 200) {
        showToast({ message: body?.message || "Note added successfully" });
        insertLog(
          `${userName} added a note for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'SUCCESS',
          ''
        );
        closeAddActionModal();
        await fetchAgencyDetails();
      } else {
        insertLog(
          `${userName} failed to add a note for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'FAILED',
          body?.message || "Unable to add note."
        );
        showAlert(body?.message || "Unable to add note.", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
      }
    } catch (error) {
      insertLog(
        `${userName} failed to add a note for agency ${agencyID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        agencyID || '',
        'INSERT',
        'WS_Agency_Details_Add_Data',
        'FAILED',
        error.message
      );
      showAlert(error.message || "Failed to add note.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const handleAddEventSubmit = async (event) => {
    event.preventDefault();
    if (!agencyID || !datasetID) {
      showToast({ message: "Please select Dataset and Agency" });
      return;
    }
    if (!validateAddEvent()) {
      return;
    }

    hud("Adding Event...");
    try {
      const response = await requestSentDS.WS_Agency_Details_Add_Data({
        agencyID,
        datasetID,
        datasetName,
        events: [
          {
            startDateTime: addEventData.startDateTime.replace("T", " ") + ":00",
            endDateTime: addEventData.endDateTime.replace("T", " ") + ":00",
            message: addEventData.message.trim(),
            updated_by_email_id: localStorage.getItem("email") || "",
          },
        ],
      });
      const body = parseApiBody(response);
      if (response?.data?.statusCode === 200) {
        showToast({ message: body?.message || "Event added successfully" });
        insertLog(
          `${userName} added an event for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'SUCCESS',
          ''
        );
        closeAddActionModal();
        await fetchAgencyDetails();
      } else {
        insertLog(
          `${userName} failed to add an event for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'FAILED',
          body?.message || "Unable to add event."
        );
        showAlert(body?.message || "Unable to add event.", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
      }
    } catch (error) {
      insertLog(
        `${userName} failed to add an event for agency ${agencyID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        agencyID || '',
        'INSERT',
        'WS_Agency_Details_Add_Data',
        'FAILED',
        error.message
      );
      showAlert(error.message || "Failed to add event.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const handleAddChequeSubmit = async (event) => {
    event.preventDefault();
    if (!agencyID || !datasetID) {
      showToast({ message: "Please select Dataset and Agency" });
      return;
    }
    if (!validateAddCheque()) {
      return;
    }

    hud("Generating Cheque...");
    try {
      const amount = parseFloat(addChequeData.amount);
      const response = await requestSentDS.WS_Agency_Details_Add_Data({
        agencyID,
        datasetID,
        datasetName,
        cheques: [
          {
            payableTo: addChequeData.payableTo.trim(),
            amount,
            amountInWords: numberToWords(amount),
            address: {
              address: addChequeData.address.trim(),
              city: addChequeData.city.trim(),
              state: addChequeData.state.trim(),
              zip: addChequeData.zip.trim(),
            },
            memo: addChequeData.memo.trim(),
            created_by_email_id: localStorage.getItem("email") || "",
          },
        ],
      });
      const body = parseApiBody(response);
      if (response?.data?.statusCode === 200) {
        showToast({ message: body?.message || "Cheque generated successfully" });
        insertLog(
          `${userName} generated a cheque for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'SUCCESS',
          ''
        );
        closeAddActionModal();
        await fetchAgencyDetails();
      } else {
        insertLog(
          `${userName} failed to generate cheque for agency ${agencyID}`,
          0,
          useremail,
          hud,
          stopHudRotation,
          showAlert,
          agencyID || '',
          'INSERT',
          'WS_Agency_Details_Add_Data',
          'FAILED',
          body?.message || "Failed to generate cheque."
        );
        showAlert(body?.message || "Failed to generate cheque.", [
          { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
        ]);
      }
    } catch (error) {
      insertLog(
        `${userName} failed to generate cheque for agency ${agencyID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        agencyID || '',
        'INSERT',
        'WS_Agency_Details_Add_Data',
        'FAILED',
        error.message
      );
      showAlert(error.message || "Failed to generate cheque.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const handleOpenCallForAction = () => {
    const request = getDefaultActionRequest();
    if (!request?.requestID || request.requestID === "NA") {
      showToast({ message: "No request available for call action" });
      return;
    }
    if (!request.agencyPhoneNumber || request.agencyPhoneNumber === "NA") {
      showAlert("Agency phone number is not available for calling.", [
        { label: "Ok", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
      return;
    }

    setSelectedActionRequest(request);
    setDialOpen(true);
  };

  const handleOpenMail = () => {
    const request = getDefaultActionRequest();
    if (!request?.requestID || request.requestID === "NA") {
      showToast({ message: "No request available for email" });
      return;
    }

    setSelectedActionRequest(request);
    setShowMailModal(true);
  };

  const handleViewCheque = async (cheque) => {
    if (!cheque?.s3Key || cheque.s3Key === "NA") {
      showToast({ message: "Cheque preview is not available" });
      return;
    }

    hud("Loading Cheque...");
    try {
      const response = await requestSentDS.WS_OutgoingRequest_Cheque_PresignedUrl_GET({ s3Key: cheque.s3Key, datasetID, datasetName });
      const body = parseApiBody(response);

      if (response?.data?.statusCode === 200 && body?.presignedUrl) {
        setChequePdfUrl(body.presignedUrl);
        setViewChequeRequestId(cheque.requestID || cheque.vinId || cheque.id || "");
        setShowViewChequeModal(true);
        return;
      }

      insertLog(
        `${userName} failed to view cheque for agency ${agencyID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        cheque.requestID || cheque.vinId || cheque.id || agencyID || '',
        'GET',
        'WS_OutgoingRequest_Cheque_PresignedUrl_GET',
        'FAILED',
        body?.message || "Failed to load cheque."
      );
      showAlert(body?.message || "Failed to load cheque.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } catch (error) {
      insertLog(
        `${userName} failed to view cheque for agency ${agencyID}`,
        0,
        useremail,
        hud,
        stopHudRotation,
        showAlert,
        cheque.requestID || cheque.vinId || cheque.id || agencyID || '',
        'GET',
        'WS_OutgoingRequest_Cheque_PresignedUrl_GET',
        'FAILED',
        error.message
      );
      showAlert(error.message || "Failed to load cheque.", [
        { label: "OK", onClick: () => { }, color: "var(--buttonColor)" }
      ]);
    } finally {
      stopHudRotation();
    }
  };

  const closeViewChequeModal = () => {
    setShowViewChequeModal(false);
    setChequePdfUrl("");
    setViewChequeRequestId("");
  };

  const getAudioUrl = async (key) => {
    const idToken = localStorage.getItem("idToken");
    const s3 = new S3Client({
      region: process.env.REACT_APP_AWS_REGION,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: process.env.REACT_APP_AWS_REGION },
        identityPoolId: process.env.REACT_APP_CONNECT_IDENTITY_POOL_ID,
        logins: {
          [`cognito-idp.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${process.env.REACT_APP_COGNITO_USERPOOLID}`]: idToken,
        },
      }),
    });

    const command = new GetObjectCommand({
      Bucket: process.env.REACT_APP_CONNECT_BUCKET,
      Key: key,
    });

    return getSignedUrl(s3, command, { expiresIn: 3600 });
  };

  const handlePlay = async (record) => {
    if (!record?.recordingKey) {
      showToast({ message: "No recording available" });
      return;
    }

    try {
      if (playingId === record.id && audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
        setModalItem(record.raw);
        setModalOpen(true);
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      const url = await getAudioUrl(record.recordingKey);
      setAudioUrl(url);
      setPlayingId(record.id);

      if (audioRef.current) {
        audioRef.current.src = url;
        await audioRef.current.play();
        setIsPlaying(true);
      }

      setModalItem(record.raw);
      setModalOpen(true);
    } catch (error) {
      showToast({ message: error.message || "Unable to play recording" });
    }
  };

  const closeAnalysisModal = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
    }

    setIsPlaying(false);
    setPlayingId(null);
    setAudioUrl(null);
    setAnalysisCall(null);
  };

  return {
    navigate,
    searchItem,
    datasets,
    agencyOptions,
    datasetID,
    datasetName,
    agencyID,
    datasetSearch,
    agencySearch,
    isDatasetDropdownOpen,
    isAgencyDropdownOpen,
    setIsDatasetDropdownOpen,
    setIsAgencyDropdownOpen,
    hasSearched,
    agencySections,
    openSections,
    viewAllLoadedSections,
    activeSectionModal,
    mailViewMode,
    setMailViewMode,
    callHistoryIncoming,
    setCallHistoryIncoming,
    selectedActionRequest,
    setSelectedActionRequest,
    dialOpen,
    setDialOpen,
    showMailModal,
    setShowMailModal,
    showViewChequeModal,
    chequePdfUrl,
    viewChequeRequestId,
    analysisCall,
    setAnalysisCall,
    modalOpen,
    setModalOpen,
    modalItem,
    audioUrl,
    setAudioUrl,
    playingId,
    setPlayingId,
    isPlaying,
    setIsPlaying,
    audioRef,
    selectedMail,
    setSelectedMail,
    showMailHeaderDetails,
    setShowMailHeaderDetails,
    mailHeaderToggleRef,
    mailHeaderDetailsRef,
    addModalType,
    setAddModalType,
    addNoteText,
    setAddNoteText,
    addEventData,
    setAddEventData,
    addActionErrors,
    addChequeData,
    setAddChequeData,
    getSenderDisplayName,
    getSenderInitial,
    formatMailDateTime,
    getDatasetLabel,
    getAgencyLabel,
    filteredDatasets,
    filteredAgencyOptions,
    selectDataset,
    handleDatasetSearchChange,
    handleDatasetKeyDown,
    selectAgency,
    handleAgencySearchChange,
    handleAgencyKeyDown,
    fetchAgencyDetails,
    handleSearch,
    toggleSection,
    openSectionModal,
    closeSectionModal,
    fetchViewAllSection,
    getNoteKey,
    isNotePinned,
    clearAddActionError,
    getCurrentDateTimeInputValue,
    closeAddActionModal,
    openAddActionModal,
    handleAddNoteSubmit,
    handleAddEventSubmit,
    handleAddChequeSubmit,
    handleOpenCallForAction,
    handleOpenMail,
    handleViewCheque,
    closeViewChequeModal,
    handlePlay,
    closeAnalysisModal
  };
};
